import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";
import Tablefilter from "./Tablefilter";
import { handleTableScroll } from "../../utils/commonFunction";
import axios from "axios";


const getStorageKey = (storageKey) => `filterState_${storageKey}`;

const DataTableComponentBackendPagination = ({
  columns,
  getDataApi,
  getFiltersApi,
  tableButton,
  fileName = "export",
  filterConfig = [],
  showExport = true,
  showFilter = true,
  showSearch = true,
  isInsidePopUp,
  isActionAlignRight,
  errorMessageMargin,
  custumcss,
  tableTitle,
  defaultSortField,
  defaultSortAsc = false,
  perPageOptions = [10, 25, 50, 100],
  LoadingComponent,
  additionalParams = {},
  storeFilters = false,
  storageKey = null,
}) => {
  const [dataArr, setDataArr] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(defaultSortField);
  const [sortOrder, setSortOrder] = useState(defaultSortAsc ? "ASC" : "DESC");

  // State for filters
  const [filterStates, setFilterStates] = useState({});
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    handleTableScroll();
  }, [loading]);

  if (storeFilters && !storageKey) {
    console.error(
      "DataTableComponentBackendPagination: storeFilters is true but no storageKey was provided. Filter persistence is disabled for this instance."
    );
  }

  // Load filter options, hydrating from localStorage first if storeFilters is enabled
  useEffect(() => {
    const loadFilters = async () => {
      if (getFiltersApi) {
        try {
          const res = await getFiltersApi();
          setFilterOptions(res.data?.data || res?.data);

          const initialFilterStates = {};
          filterConfig.forEach(filter => {
            initialFilterStates[filter.key] = { arr: [], checkObj: {} };
          });

          if (storeFilters && storageKey) {
            try {
              const stored = JSON.parse(localStorage.getItem(getStorageKey(storageKey)));
              if (stored) {
                Object.keys(initialFilterStates).forEach((key) => {
                  if (stored[key]) {
                    initialFilterStates[key] = stored[key];
                  }
                });
              }
            } catch (e) {
              // malformed or missing storage entry — fall back to defaults silently
            }
          }

          setFilterStates(initialFilterStates);
        } catch (error) {
          console.error("Error loading filters:", error);
        }
      }
    };
    loadFilters();
  }, []);

  // Persist filter selections whenever they change, once hydrated
  useEffect(() => {
    if (!storeFilters || !storageKey) return;
    if (!filterStates || Object.keys(filterStates).length === 0) return;

    localStorage.setItem(getStorageKey(storageKey), JSON.stringify(filterStates));
  }, [filterStates, storeFilters, storageKey]);

  // Load data
  useEffect(() => {
    const cancelTokenSources = [];
    const loadData = async () => {
      setLoading(true);
      cancelTokenSources.forEach(source => {
        source.cancel('New request made');
      });

      const source = axios.CancelToken.source();
      cancelTokenSources.push(source);
      try {
        const filterParams = {};
        Object.keys(filterStates).forEach(key => {
          filterParams[key] = filterStates[key].arr;
        });

        const params = {
          page,
          limit: perPage,
          key: sortKey,
          sort: sortOrder,
          name: search,
          ...filterParams,
          ...additionalParams
        };

        const res = await getDataApi(params, source.token);
        setDataArr(res.data?.data_list?.data || []);
        setTotalRows(res.data?.data_list?.total || 0);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelTokenSources.forEach(source => {
        source.cancel('Component unmounted');
      });
    };
  }, [page, perPage, sortKey, sortOrder, search, filterStates, additionalParams]);

  // Handle search debounce
  useEffect(() => {
    const timer = setTimeout(() => setSearch(debouncedTerm), 1000);
    return () => clearTimeout(timer);
  }, [debouncedTerm]);

  const handlePageChange = (pageNo) => {
    setPage(pageNo);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
  };

  const handleSort = (column, sortDirection) => {
    setSortKey(column.sortField);
    setSortOrder(sortDirection === "asc" ? "ASC" : "DESC");
  };

  const handleSearchFilter = (e) => {
    setDebouncedTerm(e.target.value);
  };

  const resetFilter = () => {
    setSearch("");
    setDebouncedTerm("");
    const resetFilters = {};
    Object.keys(filterStates).forEach(key => {
      resetFilters[key] = { arr: [], checkObj: {} };
    });
    setFilterStates(resetFilters);

    if (storeFilters && storageKey) {
      localStorage.removeItem(getStorageKey(storageKey));
    }
  };

  const exportData = async (fileType) => {
    try {
      Swal.fire({
        title: "File downloading",
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const params = {
        ...additionalParams,
        exportStatus: "true",
        page,
        limit: perPage,
        key: sortKey,
        sort: sortOrder,
        name: search,
      };

      const res = await getDataApi(params);
      const data = res.data?.data_list || [];
      const header = columns.map(col => col.name);

      if (fileType === "csv") {
        const csvString = Papa.unparse({ fields: header, data });
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });
        const blobURL = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.download = `${fileName}.csv`;
        anchor.href = blobURL;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(blobURL), 1000);
      } else if (fileType === "xlsx") {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data, { header });
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      } else if (fileType === "pdf") {
        const doc = new JsPDF();
        doc.autoTable({
          head: [header],
          body: data.map(row => header.map(col => row[col] || "-")),
          styles: {
            minCellHeight: 10,
            halign: "left",
            fontSize: 8,
          },
        });
        doc.save(`${fileName}.pdf`);
      }

      Swal.close();
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire("Error", "Failed to export data", "error");
    }
  };

  return (
    <div className="my-tickets-info-list Tickets-main-wrap">
      <div className={isInsidePopUp ? `custom-table-div filter-search-icon card-table-custom course-table-filter-sec ${isActionAlignRight ? "grade-mix-table" : ""}` : `custom-table-div filter-search-icon card card-table-custom course-table-filter-sec submisson-table-bg ${dataArr.length == 1 ? "submisson-table-item1" : ""} ${errorMessageMargin ? "error-message-margin" : ""} ${custumcss}`}>
        {tableTitle && <div className="table-title mb-3" title="Student List">{tableTitle}</div>}
        <div className="search-filter-div">
          <div className="search-filter-div-left">
            <div className="system-administration-table table-responsive">
              {showSearch && (
                <div className="table-responsive-div">
                  <div
                    id="assessment-table-main_wrapper"
                    className="dataTables_wrapper no-footer"
                  >
                    <div className="dataTables_wrapper no-footer">
                      <div className="dataTables_filter">
                        <label>
                          <input
                            type="search"
                            placeholder="Search"
                            onChange={handleSearchFilter}
                            aria-controls="assessment-table-main"
                            value={debouncedTerm}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showFilter && (
                <div className="filter-button-group">
                  <div className="filter-scroll">
                    <div className="filter-scroll-inner filter-custom-new">
                      {filterConfig.map((filter) => (
                        <Tablefilter
                          key={filter.key}
                          filterName={filter.label}
                          optionArr={filterOptions[filter.optionsKey] || []}
                          isSearchFilter={filter.isSearchFilter === true ? true : false}
                          state={filterStates[filter.key]}
                          setState={(newState) =>
                            setFilterStates(prev => ({
                              ...prev,
                              [filter.key]: newState
                            }))
                          }
                          uniqueId={filter.key}
                          renderLabelFunction={filter.renderFunction}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="reset-btn-group">
                    <div className="button-reset dropdown-comman">
                      <button
                        type="button"
                        className="btn btn-primary"
                        title="Reset"
                        onClick={resetFilter}
                      >
                        <i className="fal fa-redo"></i>Reset
                      </button>
                    </div>

                    {showExport && (
                      <div className="files-export-group">
                        <button
                          type="button"
                          className="btn btn-files"
                          onClick={() => exportData("xlsx")}
                          title="Export spreadsheet"
                        >
                          <i className="fal fa-file-excel icon"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-files"
                          onClick={() => exportData("csv")}
                          title="Export CSV"
                        >
                          <i className="fal fa-file-csv icon"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-files"
                          onClick={() => exportData("pdf")}
                          title="Export PDF"
                        >
                          <i className="fal fa-file-pdf icon"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="search-filter-div-right">
            <div className=" filter-search-bar-blk">
              <div className="add-ticket-blk button-reset dropdown-comman">
                {tableButton.map((item, index) => (
                  <div key={index}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DataTable
          paginationDefaultPage={page}
          data={dataArr}
          columns={columns}
          defaultSortAsc={defaultSortAsc}
          defaultSortField={defaultSortField}
          paginationServer
          pagination
          paginationTotalRows={totalRows}
          progressPending={loading}
          progressComponent={LoadingComponent}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          highlightOnHover={false}
          onSort={handleSort}
          paginationRowsPerPageOptions={perPageOptions}
        />
      </div>
    </div>
  );
};

export default DataTableComponentBackendPagination;